import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Layout } from 'antd';
import { CounterChartCard } from '../components/chart';
import { CounterStatusCard } from '../components/status';
import { CounterHistoryCard } from '../components/history';
import { VideoStreamCard } from '../components/video';
import { CounterSettingsModal, StopCountingModal } from '@smolt/shared/components';
import { AppHeader, AppFooter } from '@smolt/shared/components';
import {
  useCameraById,
  useUpdateCameraSettings,
  useStartCounting,
  useStopCounting,
} from '@/features/dashboard/hooks/use-camera-data';
import { useChartData, useHistoryData, useHistoryChartData } from '../hooks/use-counter-data';
import { useSlidingWindowRate } from '../hooks/use-sliding-window-rate';
import type { Camera, CameraSettings } from '@smolt/shared/types';
import type { ColumnsType } from 'antd/es/table';

interface HistoryRecord {
  key: string;
  time: string;
  inputTank?: string;
  outputTank?: string;
  duration: string;
  count: number;
  weight?: number;
  kgPerFish?: number;
}
import { useIsMobile, useBreakpoint } from '@smolt/shared/hooks';
import { SessionDisplay } from '@smolt/shared/models';
import fishscopeLogo from '@/assets/FISHSCOPE_logo.png';
import akvakimLogo from '@/assets/AKVAKAM_logo_white 2.png';
import tidepoolLogo from '@/assets/tidepool_logo_dark_thick 2.png';

/**
 * CounterPage - Pixel-perfect Counter Page
 * Matches camera-detail-page.png screenshot exactly
 * Uses AntD + Tailwind for precise visual fidelity
 */
export const CounterPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [selectedHistoryRecord, setSelectedHistoryRecord] = useState<HistoryRecord | null>(null);
  const [wasCountingRef, setWasCountingRef] = useState(false);
  const isMobile = useIsMobile();
  const breakpoint = useBreakpoint();
  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return 0;
  });

  const { data: camera, isLoading: cameraLoading } = useCameraById(id || '');

  // SessionDisplay instance caching - prevents recreation on every render
  // Uses Map to cache by camera ID and reuses instance when camera object is identical (structural sharing)
  const sessionDisplayCache = useRef<Map<string, { camera: Camera; display: SessionDisplay }>>(
    new Map()
  );

  const sessionDisplay = useMemo(() => {
    if (!camera) {
      sessionDisplayCache.current.clear();
      return null;
    }

    const cached = sessionDisplayCache.current.get(camera.id);

    // Reuse cached SessionDisplay if camera object is identical (structural sharing)
    if (cached && cached.camera === camera) {
      return cached.display;
    } else {
      const newDisplay = new SessionDisplay(camera);
      sessionDisplayCache.current.set(camera.id, { camera, display: newDisplay });
      return newDisplay;
    }
  }, [camera]);

  const { data: chartData, isLoading: chartLoading } = useChartData(
    id || '',
    sessionDisplay?.isCounting || false
  );
  const { data: historyData, isLoading: historyLoading } = useHistoryData(
    id || '',
    sessionDisplay?.isCounting || false
  );
  const { data: historyChartData } = useHistoryChartData(selectedHistoryRecord?.key || null);

  // Track window width for 1200px+ detection
  useEffect(() => {
    const updateWidth = () => {
      setWindowWidth(window.innerWidth);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const renderCount = useRef(0);
  renderCount.current += 1;

  // Get active record key (first record in historyData when counting) - memoized
  const activeRecordKey = useMemo(() => {
    return sessionDisplay?.isCounting && historyData && historyData.length > 0
      ? historyData[0].key
      : null;
  }, [sessionDisplay?.isCounting, historyData]);

  // Check if selected record is the active (currently counting) record - memoized
  const isActiveRecordSelected = useMemo(() => {
    return (
      selectedHistoryRecord && activeRecordKey && selectedHistoryRecord.key === activeRecordKey
    );
  }, [selectedHistoryRecord, activeRecordKey]);

  // Use history chart data if a non-active history record is selected, otherwise use real-time chart data - memoized
  // Gracefully handle race condition: if historyChartData is not yet loaded, fall back to chartData
  const displayChartData = useMemo(() => {
    if (selectedHistoryRecord && !isActiveRecordSelected) {
      // History record selected - prefer historyChartData if available
      // Fall back to chartData if historyChartData is empty (handles race condition during stop)
      if (historyChartData && historyChartData.length > 0) {
        return historyChartData;
      }
      // historyChartData not ready yet, keep showing chartData
      return chartData;
    }
    return chartData;
  }, [selectedHistoryRecord, isActiveRecordSelected, historyChartData, chartData]);

  const displayCount = useMemo(() => {
    return selectedHistoryRecord && !isActiveRecordSelected
      ? selectedHistoryRecord.count
      : camera?.count || 0;
  }, [selectedHistoryRecord, isActiveRecordSelected, camera?.count]);

  const displayWeight = useMemo(() => {
    return selectedHistoryRecord && !isActiveRecordSelected
      ? selectedHistoryRecord.weight
      : camera?.weight || 0;
  }, [selectedHistoryRecord, isActiveRecordSelected, camera?.weight]);

  const displayDuration = useMemo(() => {
    return selectedHistoryRecord && !isActiveRecordSelected
      ? selectedHistoryRecord.duration
      : camera?.duration || '00:00:00';
  }, [selectedHistoryRecord, isActiveRecordSelected, camera?.duration]);

  // Ref to cache sessionStart for stability across renders
  const sessionStartRef = useRef<number | null>(null);

  // Calculate sessionStart for live duration display (like History table uses camera?.duration directly)
  const sessionStart = useMemo(() => {
    const isCounting = sessionDisplay?.isCounting || false;

    // When not counting, clear the cached value
    if (!isCounting) {
      sessionStartRef.current = null;
      return null;
    }

    // If we already have a cached sessionStart, use it for stability
    if (sessionStartRef.current !== null) {
      return sessionStartRef.current;
    }

    // If duration is '00:00:00' (just started), use current time
    if (!camera?.duration || camera.duration === '00:00:00') {
      const now = Date.now();
      sessionStartRef.current = now;
      return now;
    }

    // Calculate sessionStart from camera.duration
    const [hours, minutes, seconds] = camera.duration.split(':').map(Number);
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    const calculatedStart = Date.now() - totalSeconds * 1000;
    sessionStartRef.current = calculatedStart;
    return calculatedStart;
  }, [sessionDisplay?.isCounting, camera?.duration]);

  // Calculate elapsed hours from display duration (for historical records)
  const elapsedHours = useMemo(() => {
    if (!displayDuration || displayDuration === '00:00:00') {
      return 0;
    }
    const [hours, minutes, seconds] = displayDuration.split(':').map(Number);
    return hours + minutes / 60 + seconds / 3600;
  }, [displayDuration]);

  // Sliding window rate calculation (3-second window) for real-time counting
  const isCounting = sessionDisplay?.isCounting || false;
  const slidingWindowRates = useSlidingWindowRate(
    camera?.count || 0,
    camera?.weight || 0,
    isCounting,
    { windowSizeSeconds: 5 }
  );

  // Calculate tonnes per hour:
  // - For active counting: use sliding window
  // - For historical records: use total average
  const tonnesPerHour = useMemo(() => {
    // Currently counting and viewing active record - use sliding window
    if (isCounting && (!selectedHistoryRecord || isActiveRecordSelected)) {
      return slidingWindowRates.tonnesPerHour;
    }
    // Historical record - use total average
    if (elapsedHours === 0) {
      return 0;
    }
    return (displayWeight ?? 0) / elapsedHours;
  }, [
    isCounting,
    selectedHistoryRecord,
    isActiveRecordSelected,
    slidingWindowRates.tonnesPerHour,
    displayWeight,
    elapsedHours,
  ]);

  // Calculate fish per hour:
  // - For active counting: use sliding window
  // - For historical records: use total average
  const fishPerHour = useMemo(() => {
    // Currently counting and viewing active record - use sliding window
    if (isCounting && (!selectedHistoryRecord || isActiveRecordSelected)) {
      return slidingWindowRates.fishPerHour;
    }
    // Historical record - use total average
    if (elapsedHours === 0) {
      return 0;
    }
    return displayCount / elapsedHours;
  }, [
    isCounting,
    selectedHistoryRecord,
    isActiveRecordSelected,
    slidingWindowRates.fishPerHour,
    displayCount,
    elapsedHours,
  ]);

  // Initial values for settings modal - must be at top level, not inside conditional render
  const initialSettingsValues = useMemo(
    () => ({
      cameraName: camera?.name || '',
      weightPerFish: camera?.weightPerFish,
      inputTank: camera?.inputTank || '',
      outputTank: camera?.outputTank || '',
    }),
    [camera?.name, camera?.weightPerFish, camera?.inputTank, camera?.outputTank]
  );

  const updateSettings = useUpdateCameraSettings();
  const startCounting = useStartCounting();
  const stopCounting = useStopCounting();

  const handleSettingsSave = useCallback(
    (values: CameraSettings) => {
      if (id) {
        updateSettings.mutate({ id, settings: values });
        setSettingsModalOpen(false);
      }
    },
    [id, updateSettings]
  );

  // Use ref to access latest sessionDisplay without causing re-renders
  const sessionDisplayRef = useRef(sessionDisplay);
  sessionDisplayRef.current = sessionDisplay;

  const handleStartStop = useCallback(() => {
    const currentSessionDisplay = sessionDisplayRef.current;
    if (!currentSessionDisplay || !id) return;

    if (currentSessionDisplay.isCounting || currentSessionDisplay.sessionState === 'starting') {
      setStopModalOpen(true);
    } else if (currentSessionDisplay.canStartSession) {
      startCounting.mutate(id);
    }
  }, [id, startCounting]);

  const handleStopConfirm = useCallback(() => {
    if (id) {
      stopCounting.mutate(id);
      setStopModalOpen(false);
    }
  }, [id, stopCounting]);

  // Save current camera ID to sessionStorage for hardware back button support
  useEffect(() => {
    if (id) {
      sessionStorage.setItem('lastViewedCameraId', id);
    }
  }, [id]);

  const handleBackClick = useCallback(() => {
    // Navigate to dashboard with current camera ID for focus
    navigate('/', { state: { focusCameraId: id } });
  }, [navigate, id]);

  // Handle history row click
  const handleHistoryRowClick = useCallback((record: HistoryRecord) => {
    setSelectedHistoryRecord(record);
  }, []);

  const handleSettingsClick = useCallback(() => {
    setSettingsModalOpen(true);
  }, []);

  const handleSettingsCancel = useCallback(() => {
    setSettingsModalOpen(false);
  }, []);

  const handleStopCancel = useCallback(() => {
    setStopModalOpen(false);
  }, []);

  // Auto-select first record when counting starts or stops
  useEffect(() => {
    const isCounting = sessionDisplay?.isCounting || false;

    if (isCounting && !wasCountingRef) {
      // Counting just started: select the first (active) record
      if (historyData && historyData.length > 0) {
        setSelectedHistoryRecord(historyData[0]);
      }
      setWasCountingRef(true);
    } else if (wasCountingRef && !isCounting && historyData && historyData.length > 0) {
      // Just stopped counting: select the first (most recent) record
      setSelectedHistoryRecord(historyData[0]);
      setWasCountingRef(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- wasCountingRef is a local state used for tracking previous state
  }, [sessionDisplay?.isCounting, historyData]);

  // Auto-select first history record on initial load (whether counting or not)
  // This ensures real-time data is shown when entering the page during counting
  useEffect(() => {
    if (historyData && historyData.length > 0 && !selectedHistoryRecord) {
      setSelectedHistoryRecord(historyData[0]);
    }
  }, [historyData, selectedHistoryRecord]);

  // Table columns matching screenshot - memoized to prevent recreation
  const historyColumns: ColumnsType<HistoryRecord> = useMemo(
    () => [
      {
        title: 'Time',
        dataIndex: 'time',
        key: 'time',
        width: 80,
        align: 'center',
        render: (value: string, record: HistoryRecord) => {
          // Show counting indicator only for active record (currently counting)
          const isActiveRecord = activeRecordKey && record.key === activeRecordKey;
          const isSelected = selectedHistoryRecord?.key === record.key;
          if (isActiveRecord) {
            return (
              <div
                className="relative flex items-center justify-center"
                style={{ width: '100%' }}
              >
                <span
                  className={`${breakpoint === 'largeDesktop' ? 'w-3 h-3' : 'w-2 h-2'} rounded-full bg-fs-status-recording absolute`}
                  style={{
                    left: windowWidth >= 1200 ? '0px' : isMobile ? '4px' : '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                />
                <span
                  className="text-[8px] text-fs-text-primary"
                  style={{ fontWeight: isSelected ? 'bold' : 'normal' }}
                >
                  {value || '-'}
                </span>
              </div>
            );
          }
          return (
            <span
              className="text-[8px] text-fs-text-primary"
              style={{ fontWeight: isSelected ? 'bold' : 'normal' }}
            >
              {value || '-'}
            </span>
          );
        },
      },
      {
        title: 'In',
        dataIndex: 'inputTank',
        key: 'inputTank',
        width: 32,
        align: 'center',
        render: (value: string, record: HistoryRecord) => {
          const isSelected = selectedHistoryRecord?.key === record.key;
          return (
            <span
              className="text-[8px] text-fs-text-primary"
              style={{ fontWeight: isSelected ? 'bold' : 'normal' }}
            >
              {value || '-'}
            </span>
          );
        },
      },
      {
        title: 'Out',
        dataIndex: 'outputTank',
        key: 'outputTank',
        width: 32,
        align: 'center',
        render: (value: string, record: HistoryRecord) => {
          const isSelected = selectedHistoryRecord?.key === record.key;
          return (
            <span
              className="text-[8px] text-fs-text-primary"
              style={{ fontWeight: isSelected ? 'bold' : 'normal' }}
            >
              {value || '-'}
            </span>
          );
        },
      },
      {
        title: 'Duration',
        dataIndex: 'duration',
        key: 'duration',
        width: 56,
        align: 'center',
        render: (value: string, record: HistoryRecord) => {
          const isSelected = selectedHistoryRecord?.key === record.key;
          // For active (counting) record, use real-time camera duration to match chart display
          const isActiveRecord = activeRecordKey && record.key === activeRecordKey;
          const displayValue = isActiveRecord ? (camera?.duration ?? value) : value;
          return (
            <span
              className="text-fs-text-primary text-[8px]"
              style={{ fontWeight: isSelected ? 'bold' : 'normal' }}
            >
              {displayValue || '-'}
            </span>
          );
        },
      },
      {
        title: 'Count',
        dataIndex: 'count',
        key: 'count',
        width: 56,
        align: 'center',
        render: (value: number, record: HistoryRecord) => {
          const isSelected = selectedHistoryRecord?.key === record.key;
          // For active (counting) record, use real-time camera count to match chart display
          const isActiveRecord = activeRecordKey && record.key === activeRecordKey;
          const displayValue = isActiveRecord ? (camera?.count ?? value) : value;
          return (
            <span
              className="text-fs-text-primary text-[8px]"
              style={{ fontWeight: isSelected ? 'bold' : 'normal' }}
            >
              {displayValue !== undefined && displayValue !== null
                ? Math.floor(displayValue).toLocaleString()
                : '-'}
            </span>
          );
        },
      },
      {
        title: 'Weight',
        dataIndex: 'weight',
        key: 'weight',
        width: 56,
        align: 'center',
        render: (_value: number, record: HistoryRecord) => {
          // For active (counting) record, use real-time camera weight to match chart display
          const isActiveRecord = activeRecordKey && record.key === activeRecordKey;
          const calculatedWeight = isActiveRecord
            ? (camera?.weight ?? null)
            : record?.kgPerFish && record?.count
              ? (record.kgPerFish * record.count) / 1000
              : null;
          const isSelected = selectedHistoryRecord?.key === record.key;
          return (
            <span
              className="text-fs-text-primary text-[8px]"
              style={{ fontWeight: isSelected ? 'bold' : 'normal' }}
            >
              {calculatedWeight !== null && calculatedWeight !== undefined
                ? (Math.floor(calculatedWeight * 100) / 100).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : '-'}
            </span>
          );
        },
      },
      {
        title: 'kg/fish',
        dataIndex: 'kgPerFish',
        key: 'kgPerFish',
        width: 40,
        align: 'center',
        render: (value: number, record: HistoryRecord) => {
          const isSelected = selectedHistoryRecord?.key === record.key;
          return (
            <span
              className="text-fs-text-primary text-[8px]"
              style={{ fontWeight: isSelected ? 'bold' : 'normal' }}
            >
              {value !== undefined && value !== null ? value.toFixed(2) : '-'}
            </span>
          );
        },
      },
    ],
    [
      isMobile,
      activeRecordKey,
      selectedHistoryRecord?.key,
      breakpoint,
      camera?.count,
      camera?.weight,
      camera?.duration,
    ]
  );

  // Dynamic layout sizes based on breakpoint - must be before conditional returns
  const layoutConfig = useMemo(() => {
    switch (breakpoint) {
      case 'mobile':
        return {
          width: '100%',
          maxWidth: '100%',
          height: '100vh',
          gap: '16px',
          headerHeight: 'clamp(36px, 8vh, 48px)',
        };
      case 'tablet':
        return {
          width: 'clamp(800px, 90vw, 1000px)',
          maxWidth: '1000px',
          height: 'clamp(450px, 80vh, 600px)',
          gap: '12px',
          headerHeight: '40px',
        };
      case 'desktop':
        return {
          width: 'clamp(1000px, 85vw, 1200px)',
          maxWidth: '1200px',
          height: 'clamp(600px, 85vh, 800px)',
          gap: '16px',
          headerHeight: '44px',
        };
      case 'largeDesktop':
        return {
          width: 'clamp(1400px, 95vw, 2400px)',
          maxWidth: '2400px',
          height: 'clamp(800px, 90vh, 1200px)',
          gap: '28px',
          headerHeight: '52px',
        };
      default:
        return {
          width: 'clamp(800px, 90vw, 1200px)',
          maxWidth: '1200px',
          height: 'clamp(500px, 80vh, 800px)',
          gap: '16px',
          headerHeight: '40px',
        };
    }
  }, [breakpoint]);

  if (cameraLoading) {
    return (
      <div className="min-h-screen bg-fs-page flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!camera || !sessionDisplay) {
    return (
      <div className="min-h-screen bg-fs-page flex items-center justify-center">
        <div className="text-fs-text-light text-xl">Camera not found</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100vh',
        height: isMobile ? 'auto' : 'auto',
        background: '#48484A',
        padding: isMobile ? '0' : '0',
        paddingTop: layoutConfig.headerHeight,
        paddingBottom: '0px',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Layout
        style={{
          width: layoutConfig.width,
          maxWidth: layoutConfig.maxWidth,
          height: 'auto',
          background: '#48484A',
          minHeight: `calc(100vh - ${layoutConfig.headerHeight})`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
        }}
      >
        <AppHeader
          title="Counter"
          showBackButton={true}
          onBackClick={handleBackClick}
          logoSrc={fishscopeLogo}
        />
        <div
          className="flex flex-col"
          style={{
            background: '#48484A',
            height: 'auto',
            flex: '1',
            minHeight: '0',
            overflow: 'visible',
          }}
        >
          {/* Main Content - Responsive Layout */}
          <div
            className={`flex-1 ${isMobile ? '' : ''}`}
            style={{
              minHeight: isMobile ? '0' : '0',
              padding: breakpoint === 'tablet' ? '4px 6px' : '10px 12px',
              overflow: isMobile ? 'visible' : 'visible',
              flex: isMobile ? 1 : undefined,
              display: isMobile ? 'flex' : undefined,
              flexDirection: isMobile ? 'column' : undefined,
            }}
          >
            {isMobile ? (
              <div
                className="flex flex-col"
                style={{
                  gap: layoutConfig.gap,
                  flex: 1,
                }}
              >
                {/* Video Stream Card */}
                <div>
                  <VideoStreamCard
                    cameraId={camera.id}
                    cameraName={camera.name}
                    sessionDisplay={sessionDisplay}
                    isCounting={sessionDisplay.isCounting}
                  />
                </div>

                {/* Status Card */}
                <div>
                  <CounterStatusCard
                    camera={camera}
                    onSettingsClick={handleSettingsClick}
                    onStartStop={handleStartStop}
                    tonnesPerHour={slidingWindowRates.tonnesPerHour}
                    fishPerHour={slidingWindowRates.fishPerHour}
                    isMobile={true}
                  />
                </div>

                {/* Chart Card */}
                <div style={{ flex: '0 0 auto' }}>
                  <CounterChartCard
                    data={displayChartData || []}
                    currentCount={displayCount}
                    weight={displayWeight}
                    weightPerFish={camera.weightPerFish}
                    tonnesPerHour={tonnesPerHour}
                    fishPerHour={fishPerHour}
                    sessionStart={sessionStart}
                    isCounting={sessionDisplay?.isCounting || false}
                  />
                </div>

                {/* History Table Card */}
                <div style={{ flex: '0 0 auto' }}>
                  <CounterHistoryCard
                    columns={historyColumns}
                    dataSource={historyData || []}
                    loading={historyLoading || chartLoading}
                    isMobile={true}
                    onRowClick={handleHistoryRowClick}
                    selectedRecordKey={selectedHistoryRecord?.key || null}
                  />
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: layoutConfig.gap,
                }}
              >
                {/* Left Column: Video + Status */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: layoutConfig.gap,
                  }}
                >
                  {/* Video Stream Card */}
                  <div style={{ flex: '0 0 auto' }}>
                    <VideoStreamCard
                      cameraId={camera.id}
                      cameraName={camera.name}
                      sessionDisplay={sessionDisplay}
                      isCounting={sessionDisplay.isCounting}
                    />
                  </div>

                  {/* Status Card */}
                  <div style={{ flex: '0 0 auto' }}>
                    <CounterStatusCard
                      camera={camera}
                      onSettingsClick={handleSettingsClick}
                      onStartStop={handleStartStop}
                      tonnesPerHour={slidingWindowRates.tonnesPerHour}
                      fishPerHour={slidingWindowRates.fishPerHour}
                      isMobile={false}
                    />
                  </div>
                </div>

                {/* Right Column: Chart + History */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: layoutConfig.gap,
                  }}
                >
                  {/* Chart Card */}
                  <div style={{ flex: '0 0 auto' }}>
                    <CounterChartCard
                      data={displayChartData || []}
                      currentCount={displayCount}
                      weight={displayWeight}
                      weightPerFish={camera.weightPerFish}
                      tonnesPerHour={tonnesPerHour}
                      fishPerHour={fishPerHour}
                      sessionStart={sessionStart}
                      isCounting={sessionDisplay?.isCounting || false}
                    />
                  </div>

                  {/* History Table Card */}
                  <div style={{ flex: '0 0 auto' }}>
                    <CounterHistoryCard
                      columns={historyColumns}
                      dataSource={historyData || []}
                      loading={historyLoading || chartLoading}
                      onRowClick={handleHistoryRowClick}
                      selectedRecordKey={selectedHistoryRecord?.key || null}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modals */}
          {camera && (
            <>
              <CounterSettingsModal
                open={settingsModalOpen}
                cameraName={camera.name}
                initialValues={initialSettingsValues}
                onSave={handleSettingsSave}
                onCancel={handleSettingsCancel}
              />

              <StopCountingModal
                open={stopModalOpen}
                cameraName={camera.name}
                onConfirm={handleStopConfirm}
                onCancel={handleStopCancel}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <AppFooter
          fixed={false}
          fullWidth={isMobile}
          akvakimLogoSrc={akvakimLogo}
          tidepoolLogoSrc={tidepoolLogo}
        />
      </Layout>
    </div>
  );
};
